// EXTRA SCOPE - the mark, and the offer of a shop drawing for it.
//
// Owner, 2026-10-21: "any issue added for this, system,area,level, should
// require extra scope and should trigger a shop drawing". Asked whether to
// block the issue or mark it, he chose create and mark.
//
// THE SIGN-OFF ITSELF IS DRAWN ELSEWHERE. components/Coordination/
// OwnerSignOffToggle is the switch, and the blue star on the schedule is its
// reading. This is only the consequence: the item in front of you is work
// beyond what the client accepted, and somebody should be asked to draw it.
//
// SO IT IS NOT A STAR AND NOT BLUE. The star says "signed off" and reads as
// something finished and good; this has to read as a cost that was not in the
// deal. Volcano, the word "Extra scope", and the scope that was signed named
// in the tooltip, because the first question anybody asks is "signed off by
// whom, and when".
import React from "react";
import { Modal, Tag, Tooltip } from "antd";

const dayText = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
};

/**
 * The sentence shown about an extra-scope mark.
 *
 * Pure, and exported, so the wording is tested without rendering antd - and
 * so the one sentence cannot drift between the issue page, the register and
 * the prompt.
 */
export function extraScopeLabel(ex) {
  if (!ex || !ex.isExtra) return "";
  const when = dayText(ex.signedOn);
  const who = ex.signedBy || "";
  const where = ex.scope || "This scope";
  return `Extra scope - ${where} was signed off by the owner/GC${when ? ` on ${when}` : ""}${who ? ` by ${who}` : ""}`;
}

/** Read-only, for an issue header, a drawing row or an RFI. */
export function ExtraScopeChip({ extraScope, small = false }) {
  const ex = extraScope || null;
  if (!ex || !ex.isExtra) return null;
  // THE SIGN-OFF BEHIND THIS MARK HAS SINCE BEEN WITHDRAWN (cc-96b09c,
  // 2026-10-21). The mark stays - somebody was told this was a variation, and
  // may have raised a drawing for it - but the live "signed off" column on the
  // same row is now empty, so without this sentence the two read as a
  // contradiction and nobody knows which to believe.
  const gone = ex.signOffWithdrawn || null;
  const withdrawn = gone
    ? ` The sign-off it was raised under was withdrawn${dayText(gone.at) ? ` on ${dayText(gone.at)}` : ""}${gone.by ? ` by ${gone.by}` : ""}.`
    : "";
  const needs = ex.shopDrawingRequired
    ? " A shop drawing has not been raised for it yet."
    : (ex.shopDrawingDeclinedAt
      ? ` Somebody decided no drawing was needed${ex.shopDrawingDeclinedReason ? `: ${ex.shopDrawingDeclinedReason}` : ""}.`
      : " A shop drawing has been raised for it.");
  return (
    <Tooltip title={`${extraScopeLabel(ex)}, so this is work beyond the agreed scope.${needs}${withdrawn}`}>
      <Tag data-testid="extra-scope-chip" color="volcano" style={{ fontSize: small ? 11 : 12, marginRight: 0 }}>
        Extra scope{gone ? " - sign-off withdrawn" : (ex.shopDrawingRequired ? " - needs a shop drawing" : "")}
      </Tag>
    </Tooltip>
  );
}

/**
 * The prompt, once an issue has been created in signed-off scope.
 *
 * DISMISSIBLE ON PURPOSE. The issue is already created and marked - the owner
 * chose create-and-mark over blocking - so this is an offer. A prompt nobody
 * can decline is a prompt everybody learns to click through, and then the one
 * that mattered gets clicked through too.
 */
export function askForShopDrawing({ message: msg, onRaise, onDecline }) {
  Modal.confirm({
    title: "This issue is extra scope - raise a shop drawing?",
    content: (
      <div style={{ fontSize: 13 }}>
        <p>{msg}</p>
        <p style={{ marginBottom: 0 }}>
          The issue has been created and marked as extra scope. A shop drawing is how that extra
          scope gets drawn, priced and signed for.
        </p>
      </div>
    ),
    okText: "Request a shop drawing",
    cancelText: "Not for this one",
    onOk: onRaise,
    onCancel: onDecline,
  });
}

/**
 * "Anything you raise here is extra scope" - shown while somebody is still
 * filling the form in.
 *
 * SAID BEFOREHAND, NOT ONLY AFTER. Telling somebody their issue is a
 * variation once they have filed it is worse than telling them while they can
 * still choose how to word it. `signOff` is utils/ownerSignOff's own view of
 * the toggle, as the sign-off API returns it.
 */
export function ExtraScopeNotice({ signOff }) {
  const so = signOff || null;
  if (!so || so.planned) return null;
  return (
    <div
      data-testid="extra-scope-notice"
      style={{
        border: "1px solid #fed7aa", background: "#fff7ed", color: "#9a3412",
        borderRadius: 6, padding: "6px 10px", fontSize: 12, marginBottom: 8,
      }}
    >
      <b>{so.scope || "This scope"} was signed off by the owner/GC{so.date ? ` on ${so.date}` : ""}.</b>{" "}
      Anything raised here is extra scope, and should be covered by a shop drawing.
    </div>
  );
}
